import { Link } from 'gatsby'
import React from 'react'
import styled, { css } from 'styled-components'

import { assignPrefix } from '../../../constants'
import { defaultTheme as theme } from '../../../theme'
// Local
import { AuthorMetaProps, MdxSeriesNode } from '../../../types'
import { abstractHtml } from '../../../utils/get-html'
import stringBetween from '../../../utils/stringBetween'
import { mobile } from '../../../utils/styles/media'
import Badge from '../../shared/Badge'
import { Space } from '../../shared/Space'
import SeriesListing from '../SeriesListing'
import AuthorMeta from './AuthorMeta'
import { Container } from './styles'

type HeaderProps = {
  date?: string
  title?: React.ReactNode
  titleText?: string
  url: string
  series?: MdxSeriesNode
  indexInSeries?: number
  abstract?: React.ReactNode
  heroImage?: string
  heroImageAlt?: string
  authors?: AuthorMetaProps[]
  interaction?: boolean
  className?: string
  style?: React.CSSProperties
}

const Header = ({
  date,
  title,
  titleText,
  url,
  series,
  indexInSeries,
  abstract,
  heroImage,
  heroImageAlt,
  authors,
  interaction,
  ...props
}: HeaderProps) => {
  let centerText = ''
  if (abstract?.toString().includes('<center>')) {
    centerText = stringBetween('<center>', '</center>', abstract?.toString())
  }

  return (
    <Wrapper {...props}>
      <Space height={56} heightOnMobile={40} />
      <Container>
        <CenteredBox>
          {/* [TODO]: add Guest Post type */}
          {/* [TODO]: add Customer Story type */}
          {date && <Date>{date}</Date>}
          {title && <Title>{title}</Title>}
          {series && (
            <FlexRow>
              <Link to={series.fields.slug}>
                <Badge>Series</Badge>
              </Link>
            </FlexRow>
          )}
          <SpaceBetween>{authors && <AuthorMeta authors={authors} />}</SpaceBetween>
        </CenteredBox>
        {abstract && (
          <Abstract>
            <center dangerouslySetInnerHTML={{ __html: centerText }} />
            <div dangerouslySetInnerHTML={{ __html: abstractHtml(abstract.toString()) }}></div>
          </Abstract>
        )}
        {heroImage && (
          <Image>
            <img src={assignPrefix(heroImage)} alt={heroImageAlt} />
          </Image>
        )}
        {series && <SeriesListing series={series} index={indexInSeries} />}
      </Container>
    </Wrapper>
  )
}

export default Header

// Styles
const Wrapper = styled.header`
  overflow: hidden;
`

const CenteredBox = styled.div`
  text-align: center;
`

const FlexRow = styled.div`
  padding: 0.75rem 0 0.5rem;
  ${Badge} {
    margin-right: 0.5rem;
  }
`

const SpaceBetween = styled.div`
  display: flex;
  margin: 1rem 0;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`
const Date = styled.p`
  font-size: 0.875rem;
  color: ${(p) => p.theme.primaryLight2};
  margin: 0 0 1.5rem 0;
`

const Title = styled.h1`
  margin: 0 auto 0;
  font-size: 56px;
  font-weight: bold;
  color: ${(p) => p.theme.primaryDark1};
  line-height: 1.2;

  ${mobile(css`
    font-size: 28px;
  `)};

  pre {
    display: inline-block;
    background: ${(p) => p.theme.primaryLight6} !important;
    border-radius: ${(p) => p.theme.radius}px;
    padding: 10px !important;
    font-size: 32px;
    line-height: 1;
    color: ${(p) => p.theme.primary};
    margin-bottom: 0;
    margin-top: 0;
    margin-right: 4px;
  }
`

const Abstract = styled.div`
  p {
    font-size: 24px;
    line-height: 1.5;
    color: ${theme.colors.text};
    margin-top: 29px;
  }
  a {
    font-size: inherit;
    color: ${theme.colors.text};
    background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.68) 50%, rgba(0, 0, 0, 0) 50%);
    background-repeat: repeat-x;
    background-size: 2px 0.1em;
    background-position: 0 1.13em;
    code {
      position: relative;
      text-decoration: underline;
      z-index: 1;
      &::after {
        z-index: -1;
        content: '';
        position: absolute;
        background: white;
        left: -2px;
        right: -2px;
        height: 4px;
        bottom: 3px;
      }
    }
  }
  code {
    background: rgb(237, 242, 247);
    border-radius: 4px;
    padding: 0.2em 0.4em;
    margin: 0px;
    font-size: 0.875em;
    line-height: 1;
    font-family: monospace;
  }
  ul,
  ol {
    margin-top: 29px;
    ${mobile(css`
      margin-top: 21px;
    `)};
    padding: 0px;
    list-style: none none;
    li {
      font-size: 18px;
      line-height: 1.7;
      color: rgb(45, 55, 72);
      margin-left: 20px;
      margin-bottom: 8px;
      color: ${theme.colors.black};
      &:before {
        content: '•';
        font-size: 24px;
        line-height: 28px;
        vertical-align: middle;
        padding-right: 8px;
        position: absolute;
        display: inline-block;
        -webkit-box-sizing: border-box;
        box-sizing: border-box;
        width: 78px;
        margin-left: -78px;
        text-align: right;
      }
    }
  }
`

const Image = styled.div`
  overflow: hidden;
  border-radius: ${(p) => p.theme.radiusBig}px;

  margin-top: 64px;
  margin-bottom: 50px;
  margin-left: -52px;
  margin-right: -52px;

  ${mobile(css`
    margin-top: 32px;
    margin-bottom: 24px;
    margin-left: -12px;
    margin-right: -12px;
  `)};

  img {
    display: block;
    width: 100%;
    height: auto;
    object-fit: cover;
    object-position: center;
  }
`
